import { useCallback, useEffect, useMemo, useRef } from 'react'
import { SearchItemLabel, usePopup } from '..'
import { intl } from '../../../../../i18n'
import { SelectOption } from '../../../../model/selectOption'
import ProcessScopeSearchSelectPopper from '../../../../features/ProcessScopeSearchSelectPopper/ProcessScopeSearchSelectPopper'
import { isDescendantOf } from '../../../../features/ProcessScopeSearchSelectPopper/treeUtils'

type ProcessScopeConditionItemLabelProps = {
  value: string[] | undefined
  onChange: (newValue: string[] | undefined) => void
  onClose: () => void
  search: () => void
  isLoading: boolean
  options: SelectOption[]
  parentMap: Record<string, string>
  optionsLoading: boolean
  isDeliverableSelected: boolean
  includeDeliverableOptions: boolean
  onToggleIncludeDeliverableOptions: (checked: boolean) => void
  isToggleDisabled: boolean
}

export const ProcessScopeConditionItemLabel = ({
  value,
  onChange,
  onClose,
  search,
  isLoading,
  options,
  parentMap,
  optionsLoading,
  isDeliverableSelected,
  includeDeliverableOptions,
  onToggleIncludeDeliverableOptions,
  isToggleDisabled,
}: ProcessScopeConditionItemLabelProps) => {
  const { ref, open, openPopup, closePopup } = usePopup()

  const selectedValues = useMemo(() => new Set(value || []), [value])

  const valueNames = useMemo(() => {
    return options
      .filter(op => (value || []).includes(op.value))
      .map(op => op.name)
  }, [value, options])

  const onClear = useCallback(() => {
    onChange(undefined)
    onClose()
  }, [onChange, onClose])

  const onClosePopup = useCallback(() => {
    search()
    closePopup()
  }, [search, closePopup])

  const valueRef = useRef(value)
  valueRef.current = value

  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  const handleToggleItem = useCallback(
    (itemValue: string) => {
      const current = new Set(valueRef.current || [])
      if (current.has(itemValue)) {
        current.delete(itemValue)
      } else {
        current.add(itemValue)
        // 親を選択した場合、配下の選択を解除する
        for (const selected of [...current]) {
          if (
            selected !== itemValue &&
            isDescendantOf(selected, itemValue, parentMap)
          ) {
            current.delete(selected)
          }
        }
      }
      const newValue = Array.from(current)
      const result = newValue.length > 0 ? newValue : undefined
      onChangeRef.current(result)
      if (newValue.length === 0) {
        search()
        closePopup()
        onClose()
      }
    },
    [parentMap, search, closePopup, onClose]
  )

  // valueが空になっても本コンポーネント自体はアンマウントされず、
  // usePopupのopen状態がtrueのまま残り得る。その状態で別の条件が
  // 追加されて再びvalueが入ると、ポップオーバーが開いた状態のまま
  // 突然現れてしまう（disablePortal={false}のためモーダルの上にも
  // 重なる）ため、経路によらずvalueが空になった時点で必ず閉じる
  useEffect(() => {
    if (!value || value.length === 0) {
      closePopup()
    }
  }, [value, closePopup])

  if (!value || value.length === 0) return <></>
  return (
    <>
      <SearchItemLabel
        ref={ref}
        active={open}
        onClick={openPopup}
        onClear={onClear}
        isLoading={isLoading}
      >
        {optionsLoading ? (
          intl.formatMessage({
            id: 'wbsItemSearchFilterPanel.processScope.loading',
          })
        ) : (
          <>
            {intl.formatMessage({
              id: isDeliverableSelected
                ? 'wbsItemSearchFilterPanel.processScope.withDeliverable'
                : 'wbsItemSearchFilterPanel.processScope',
            })}
            : {valueNames.join(', ')}
          </>
        )}
      </SearchItemLabel>
      {open && !isLoading && (
        <ProcessScopeSearchSelectPopper
          open={open && !isLoading}
          anchorEl={ref.current || undefined}
          options={options}
          parentMap={parentMap}
          isLoading={optionsLoading}
          selectedValues={selectedValues}
          onToggleItem={handleToggleItem}
          onClose={onClosePopup}
          disablePortal={false}
          includeDeliverableOptions={includeDeliverableOptions}
          onToggleIncludeDeliverableOptions={onToggleIncludeDeliverableOptions}
          isToggleDisabled={isToggleDisabled}
        />
      )}
    </>
  )
}
